import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { addToCart } from '../../redux/slices/cartSlice';
import { authorizeDownload } from '../../redux/slices/ebookDownloadSlice';
import ProductCard from '../../components/ProductCard';
import { PRODUCTS_DATABASE } from '../../data/products';
import {
  PageContainer,
  ContentWrapper,
  ProductLayout,
  ProductImage,
  ProductInfo,
  ProductTitle,
  PriceSection,
  PriceTag,
  OriginalPrice,
  Discount,
  Description,
  ButtonGroup,
  Button,
  FullDescription,
  RelatedProducts,
  RelatedTitle,
  NotFoundTitle,
  RelatedProductsGrid,
  ViewMoreContainer,
} from './ProductDetails.styles';


// parsea o HTML de `fullDescription` em seções { title, items, level }
function parseFullDescription(html) {
  if (!html) return [];
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const children = Array.from(doc.body.children);
    const sections = [];

    for (let i = 0; i < children.length; i++) {
      const node = children[i];
      const tag = node.tagName && node.tagName.toLowerCase();

      if (tag === 'h3' || tag === 'h4') {
        const title = node.textContent.trim();
        const level = tag;
        const next = children[i + 1];
        const items = [];

        if (next && next.tagName && next.tagName.toLowerCase() === 'ul') {
          const liNodes = Array.from(next.querySelectorAll('li'));
          liNodes.forEach((li) => items.push(li.textContent.trim()));
          i++; // pular o UL processado
        }

        sections.push({ title, items, level });
      }
    }

    return sections;
  } catch (err) {
    return [];
  }
}


const ProductDetailsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const { productId } = useParams();

  const [addedToCart, setAddedToCart] = useState(false);

  // Garantir que ao entrar na página de produto a posição do scroll volte ao topo
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch (e) {
      // fallback simples
      window.scrollTo(0, 0);
    }
  }, [productId]);

  // Encontrar o produto
  const product = PRODUCTS_DATABASE.find((p) => p.id === productId);

  // hooks devem ser chamados incondicionalmente (antes de qualquer return)
  const productFullDescription = product ? product.fullDescription : '';
  const sections = useMemo(() => parseFullDescription(productFullDescription), [productFullDescription]);

  // estado global: quantos itens mostrar no total (entre todas as seções)
  const [visibleTotal, setVisibleTotal] = useState(7);

  // total de músicas disponível
  const totalItems = useMemo(() => sections.reduce((sum, s) => sum + ((s.items && s.items.length) || 0), 0), [sections]);

  const handleLoadMore = () => {
    // ao clicar, mostra todas as músicas disponíveis
    setVisibleTotal(totalItems);
  };

  if (!product) {
    return (
      <PageContainer>
        <ContentWrapper>
          <NotFoundTitle>
            Produto não encontrado
          </NotFoundTitle>
        </ContentWrapper>
      </PageContainer>
    );
  }

  const discount = product.originalPrice && product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const handleAddToCart = () => {
    const cartItem = {
      id: product.id,
      title: product.title,
      price: product.price,
      quantity: 1,
      category: product.category,
    };

    dispatch(addToCart(cartItem));
    setAddedToCart(true);

    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/checkout');
  };

  const handleFreeDownload = () => {
    // Verificar se usuário está autenticado
    if (!token) {
      navigate('/login', { state: { from: `/produto/${product.id}` } });
      return;
    }

    // Autorizar download automático para produtos gratuitos
    dispatch(authorizeDownload({
      ebookId: product.id,
      ebookTitle: product.title,
      paymentInfo: { method: 'free' }
    }));

    // Fazer download direto da URL
    window.location.href = product.downloadUrl;
  };

  const relatedProducts = (() => {
    const related = [];
    
    // Se não é a coleção completa, adiciona como primeiro produto relacionado
    if (product.id !== 'colecao-completa-13-1') {
      const complete = PRODUCTS_DATABASE.find(p => p.id === 'colecao-completa-13-1');
      if (complete) related.push(complete);
    }
    
    // Adiciona produtos da mesma categoria (exceto o produto atual e coleção completa)
    const sameCategory = PRODUCTS_DATABASE.filter(
      (p) => p.category === product.category && p.id !== product.id && p.id !== 'colecao-completa-13-1'
    ).slice(0, 5);
    
    related.push(...sameCategory);
    return related;
  })();

  

  return (
    <PageContainer>
      <ContentWrapper>
        <ProductLayout>
          <ProductImage>
            {product.image ? <img src={product.image} alt={product.title} /> : '📚'}
          </ProductImage>

          <ProductInfo>
            <ProductTitle>{product.title}</ProductTitle>

                    <PriceSection>
                      {(product.price === 0 || product.isFree) ? (
                        <PriceTag>Grátis</PriceTag>
                      ) : (
                        <PriceTag>R$ {(product.price / 100).toFixed(2)}</PriceTag>
                      )}

                      {product.originalPrice > product.price && product.originalPrice > 0 && (
                        <>
                          <OriginalPrice>R$ {(product.originalPrice / 100).toFixed(2)}</OriginalPrice>
                          {discount > 0 && <Discount>{discount}% OFF</Discount>}
                        </>
                      )}
                    </PriceSection>

            <Description>{product.description}</Description>



            <ButtonGroup>
              {product.isFree && product.downloadUrl ? (
                <Button 
                  className="primary"
                  onClick={handleFreeDownload}
                >
                  ⬇️ Baixar grátis
                </Button>
              ) : (
                <>
                  <Button className="secondary" onClick={handleAddToCart}>
                    {addedToCart ? '✓ Adicionado ao carrinho' : 'Adicionar ao carrinho'}
                  </Button>
                  <Button className="primary" onClick={handleBuyNow}>
                    Comprar agora
                  </Button>
                </>
              )}
            </ButtonGroup>

            {sections && sections.length > 0 && (
              <FullDescription>
                {(() => {
                  let remaining = visibleTotal;
                  return (
                    <>
                      {sections.map((s, idx) => {
                        const count = Math.max(0, Math.min((s.items || []).length, remaining));
                        remaining -= count;
                        return (
                          <div key={idx}>
                            {s.level === 'h3' ? <h3>{s.title}</h3> : <h4>{s.title}</h4>}
                            <ul>
                              {(s.items || []).slice(0, count).map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}

                      {totalItems > 7 && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                          {visibleTotal < totalItems ? (
                            <Button className="secondary" onClick={handleLoadMore}>Ver mais</Button>
                          ) : (
                            <Button className="secondary" onClick={() => setVisibleTotal(7)}>Ver menos</Button>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </FullDescription>
            )}
          </ProductInfo>
        </ProductLayout>

        {relatedProducts.length > 0 && (
          <RelatedProducts>
            <RelatedTitle>Produtos <span>Relacionados</span></RelatedTitle>
            <RelatedProductsGrid>
              {relatedProducts.slice(0, 2).map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </RelatedProductsGrid>
            <ViewMoreContainer onClick={() => navigate('/loja')}>
              <span className="text">Ver mais</span>
              <span className="arrow">→</span>
            </ViewMoreContainer>
          </RelatedProducts>
        )}
      </ContentWrapper>
    </PageContainer>
  );
};

export default ProductDetailsPage;
